import React, { useState, useEffect } from 'react';
import { X, CreditCard, Loader } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '../context/CurrencyContext';
import { trackPaymentInitiated, trackPaymentSuccess } from '../lib/analytics';
import { PayPalBadge } from './PayPalBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PaymentModal = ({ booking, totalAmount, onClose, onSuccess }) => {
  const { currency, formatPrice, convertToCurrency } = useCurrency();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paypalError, setPaypalError] = useState(null);

  // Package prices are ALWAYS stored in INR in database
  // totalAmount here is also in INR (base currency)
  const baseAmountINR = totalAmount; // This is always INR from database

  // PayPal supports a specific set of currencies. Use the user's currency if supported,
  // otherwise fall back to USD. Rate is the SAME live rate used across the site, so
  // what user sees == what PayPal charges.
  const PAYPAL_SUPPORTED = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD', 'JPY', 'CHF', 'NZD', 'THB'];
  const paypalCurrency = PAYPAL_SUPPORTED.includes(currency) ? currency : 'USD';
  const paypalAmount = convertToCurrency(baseAmountINR, paypalCurrency);

  const paypalSymbolMap = {
    USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', SGD: 'S$',
    JPY: '¥', CHF: 'Fr', NZD: 'NZ$', THB: '฿',
  };
  const paypalSymbol = paypalSymbolMap[paypalCurrency] || '$';

  // Debug logging
  useEffect(() => {
    console.log('💳 Payment Modal Loaded');
    console.log('PAYPAL_CLIENT_ID:', PAYPAL_CLIENT_ID);
    console.log('Amount:', paypalAmount, paypalCurrency);
    console.log('Booking ID:', booking.id);
  }, []);

  // Auto-select payment method based on currency
  useEffect(() => {
    if (currency === 'INR') {
      setPaymentMethod('razorpay');
    } else {
      setPaymentMethod('paypal');
    }
  }, [currency]);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Handle Razorpay Payment
  const handleRazorpayPayment = async () => {
    setProcessing(true);
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setProcessing(false);
        return;
      }

      // Create order
      const orderResponse = await axios.post(`${API}/payments/razorpay/create-order`, {
        amount: baseAmountINR, // Always send INR amount
        currency: 'INR',
        booking_id: booking.id
      });

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        order_id: orderResponse.data.order_id,
        name: 'JMD WILDLIFE',
        description: `Booking for ${booking.package_name}`,
        image: 'https://images.pexels.com/photos/1926335/pexels-photo-1926335.jpeg?auto=compress&cs=tinysrgb&w=200',
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: booking.id
            });
            
            trackPaymentSuccess(booking, baseAmountINR, 'INR', response.razorpay_payment_id);
            toast.success('Payment successful! Booking confirmed.');
            onSuccess();
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
          setProcessing(false);
        },
        prefill: {
          name: booking.name,
          email: booking.email,
          contact: booking.phone
        },
        theme: {
          color: '#059669' // Green theme
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Razorpay payment error:', error);
      toast.error('Failed to initiate payment');
      setProcessing(false);
    }
  };

  // PayPal payment options with error handling
  const paypalOptions = {
    clientId: PAYPAL_CLIENT_ID || "test",
    currency: paypalCurrency,
    intent: 'capture',
    components: 'buttons',
    debug: true // Enable debug mode
  };

  console.log('PayPal Options:', paypalOptions);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-emerald-600 p-6 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-white">Complete Payment</h2>
            <p className="font-body text-sm text-white/80 mt-1">{booking.package_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-amber-400 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Booking Summary */}
          <div className="bg-sand p-4 rounded-lg mb-6">
            <h3 className="font-heading text-lg text-navy mb-3">Booking Summary</h3>
            <div className="space-y-2 font-body text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Package:</span>
                <span className="font-semibold text-navy">{booking.package_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span className="font-semibold text-navy">{booking.preferred_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Guests:</span>
                <span className="font-semibold text-navy">{booking.guests}</span>
              </div>
              <div className="flex justify-between border-t border-gray-300 pt-2 mt-2 items-center">
                <span className="text-gray-800 font-semibold uppercase tracking-wider text-xs">Total Amount:</span>
                <span className="price-luxe-bold text-3xl text-emerald-700">
                  {formatPrice(baseAmountINR)}
                </span>
              </div>
              {paymentMethod === 'paypal' && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-md mt-2">
                  <p className="text-xs text-blue-800 font-body">
                    💳 <span className="font-bold">PayPal will charge: <span className="price-luxe-bold text-base">{paypalSymbol}{paypalAmount.toLocaleString(undefined, {minimumFractionDigits: paypalCurrency==='JPY'?0:2, maximumFractionDigits: paypalCurrency==='JPY'?0:2})} {paypalCurrency}</span></span>
                    {currency !== paypalCurrency && (
                      <>
                        <br />
                        <span className="text-blue-600">
                          (Live rate conversion from ₹{baseAmountINR.toLocaleString()} INR)
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="mb-6">
            <h3 className="font-heading text-lg text-navy mb-4">Select Payment Method</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Razorpay Option - Show for INR */}
              {(currency === 'INR' || paymentMethod === 'razorpay') && (
                <button
                  onClick={() => setPaymentMethod('razorpay')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    paymentMethod === 'razorpay'
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-300 hover:border-emerald-400'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'razorpay' ? 'border-emerald-600' : 'border-gray-400'
                    }`}>
                      {paymentMethod === 'razorpay' && (
                        <div className="w-3 h-3 rounded-full bg-emerald-600"></div>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-body font-semibold text-navy">Razorpay</p>
                      <p className="text-xs text-gray-600">UPI, Cards, Net Banking, Wallets</p>
                    </div>
                  </div>
                </button>
              )}

              {/* PayPal Option - Show for international currencies */}
              {currency !== 'INR' && (
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    paymentMethod === 'paypal'
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-300 hover:border-emerald-400'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'paypal' ? 'border-emerald-600' : 'border-gray-400'
                    }`}>
                      {paymentMethod === 'paypal' && (
                        <div className="w-3 h-3 rounded-full bg-emerald-600"></div>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-body font-semibold text-navy">PayPal</p>
                      <p className="text-xs text-gray-600">Secure international payments</p>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Payment Buttons */}
          <div className="space-y-4">
            {paymentMethod === 'razorpay' && (
              <button
                onClick={handleRazorpayPayment}
                disabled={processing}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full font-bold tracking-wide transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {processing ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard size={20} />
                    <span>Pay with Razorpay</span>
                  </>
                )}
              </button>
            )}

            {paymentMethod === 'paypal' && !processing && (
              <div>
                {!PAYPAL_CLIENT_ID ? (
                  <div className="bg-red-50 border-2 border-red-200 p-4 rounded-lg">
                    <p className="text-red-800 font-body text-sm">
                      ⚠️ PayPal configuration missing. Please contact support.
                    </p>
                  </div>
                ) : (
                  <PayPalScriptProvider 
                    options={paypalOptions}
                    deferLoading={false}
                  >
                    <div className="text-center mb-3">
                      <PayPalBadge variant="compact" />
                    </div>
                    <div className="min-h-[150px]">
                      <PayPalButtons
                        style={{
                          layout: 'vertical',
                          color: 'gold',
                          shape: 'pill',
                          label: 'pay',
                          height: 48
                        }}
                        forceReRender={[paypalAmount]}
                        fundingSource={undefined}
                        createOrder={async () => {
                          try {
                            console.log('Creating PayPal order...');
                            trackPaymentInitiated('paypal', paypalAmount, paypalCurrency);
                            const response = await axios.post(`${API}/payments/paypal/create-order`, {
                              amount: paypalAmount,
                              currency: paypalCurrency,
                              booking_id: booking.id
                            });
                            console.log('PayPal order created:', response.data.order_id);
                            return response.data.order_id;
                          } catch (error) {
                            console.error('PayPal order creation failed:', error);
                            toast.error('Failed to create PayPal order. Please try again.');
                            throw error;
                          }
                        }}
                        onApprove={async (data) => {
                          try {
                            console.log('PayPal payment approved:', data);
                            setProcessing(true);
                            await axios.post(`${API}/payments/paypal/capture`, {
                              order_id: data.orderID,
                              booking_id: booking.id
                            });
                            trackPaymentSuccess(booking, paypalAmount, paypalCurrency, data.orderID);
                            toast.success('Payment successful! Booking confirmed.');
                            onSuccess();
                          } catch (error) {
                            console.error('PayPal capture failed:', error);
                            toast.error('Payment processing failed');
                          } finally {
                            setProcessing(false);
                          }
                        }}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          toast.error('PayPal payment failed. Please try again.');
                          setPaypalError(err.message || 'PayPal error occurred');
                        }}
                        onCancel={() => {
                          console.log('PayPal payment cancelled');
                          toast.info('Payment cancelled');
                        }}
                        onInit={(data, actions) => {
                          console.log('PayPal buttons initialized');
                        }}
                      />
                    </div>
                    {paypalError && (
                      <div className="mt-4 bg-yellow-50 border border-yellow-200 p-3 rounded">
                        <p className="text-xs text-yellow-800">
                          ⚠️ {paypalError}
                        </p>
                      </div>
                    )}
                  </PayPalScriptProvider>
                )}
              </div>
            )}
          </div>

          {/* Security Note */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center font-body">
              🔒 Your payment information is secure and encrypted. We never store your card details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
